// ReviewPage.jsx - 디자인 시스템 적용 버전
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaClipboardList, FaSearch, FaUndo, FaTrash, FaCheckCircle, FaHourglassHalf, FaTimes, FaBan } from "react-icons/fa";
import $ from "jquery";

import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import { getReviewRequest, requestMultiDelete, getResizeFileDownload } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import useQueryParams from "utils/useQueryParams";
import useSyncPageWithQuery from 'utils/useSyncPageWithQuery';
import { catchError, changeState, commonModalSetting } from "JS/common";

// 디자인 시스템 컴포넌트 임포트
import PageContainer from "Components/UI/PageContainer";
import SearchCard from "Components/UI/SearchCard";
import FormField from "Components/UI/FormField";
import Input from "Components/UI/Input";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";
import Badge from "Components/UI/Badge";
import StatusIndicator from "Components/UI/StatusIndicator";
import DateRangeField from "Components/UI/DateRangeField";
import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { commonStyles, designTokens, listPageStyles } from "utils/designSystem";

const ReviewPage = () => {
  const navigate = useNavigate();
  useMarkMenuSeen("review_self");
  const [resetTrigger, setResetTrigger] = useState(false);
  const { getQueryString, getQueryParamsObject, updateQueryParams, resetQueryParams } = useQueryParams();
  const today = new Date();

  const [selfData, setSelfData] = useState();
  const [pageInfo, setPageInfo] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      page: Number(queryParams.page) || 1,
      limit: 10,
      totalPage: 1,
    };
  });
  useSyncPageWithQuery(setPageInfo)
  const [searchData, setSearchData] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      markName: queryParams.markName || "",
      templateName: queryParams.templateName || "",
      applicantName: queryParams.applicantName || "",
      startDate: queryParams.startDate || "",
      endDate: queryParams.endDate || "",
      markType: queryParams.markType || "all",
    }
  });

  const [, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  let prevent = false;
  const [idArr, setIdArr] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  // 추가 스타일 (디자인 시스템 보완)
  const pageStyles = {
    actionSection: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: designTokens.spacing.lg
    },
    markImageContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm
    },
    markImage: {
      width: '40px',
      height: '40px',
      objectFit: 'contain',
      borderRadius: designTokens.borderRadius.sm,
      border: `1px solid ${designTokens.colors.borderLight}`
    },
    userInfo: listPageStyles.userInfo,
    userName: {
      fontWeight: '500',
      marginBottom: '2px',
      color: designTokens.colors.text.primary
    },
    userId: {
      fontSize: '12px',
      color: designTokens.colors.text.tertiary
    },
    checkboxContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  };

  // 상표 유형별 배지 variant 매핑
  const getMarkTypeBadgeVariant = (type) => {
    const variants = {
      text: 'primary',
      figure: 'success',
      compound: 'warning'
    };
    return variants[type] || 'primary';
  };

  // 상표 유형 텍스트 반환
  const getMarkTypeText = (type) => {
    const textMap = {
      text: '문자상표',
      figure: '도형상표',
      compound: '복합상표'
    };
    return textMap[type] || '문자상표';
  };

  // 검토 상태 설정
  const getReviewStatusConfig = (status) => {
    const configs = {
      request: {
        variant: 'pending',
        icon: <FaHourglassHalf size={12} />,
        text: '요청'
      },
      processing: {
        variant: 'pending',
        icon: <FaHourglassHalf size={12} />,
        text: '진행중'
      },
      completed: {
        variant: 'valid',
        icon: <FaCheckCircle size={12} />,
        text: '완료'
      },
      rejected: {
        variant: 'invalid',
        icon: <FaTimes size={12} />,
        text: '거절'
      },
      canceled: {
        variant: 'invalid',
        icon: <FaBan size={12} />,
        text: '취소'
      }
    };
    return configs[status] || configs.request;
  };

  const getList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => (prevent = false), 200);

    setSelfData([]);

    const result = await getReviewRequest(pageInfo, searchData);

    if (typeof result === "object") {
      const data = result?.data?.data;
      setSelfData(data);

      const imageFetches = data.map(async (item) => {
        let fileName = "";
        let originalName = "";

        if (item.mark_type === "figure" || item.mark_type === "text") {
          fileName = item.image_filename;
          originalName = item.image_original_filename;
        } else if (item.mark_type === "compound") {
          fileName = item.compound_image_filename;
          originalName = "복합상표";
        }

        if (fileName && originalName) {
          const url = await fetchUploadedImage(fileName, originalName);
          return { id: item.id, url };
        }

        return { id: item.id, url: null };
      });

      const resolvedImages = await Promise.all(imageFetches);

      const imageMap = {};
      resolvedImages.forEach(({ id, url }) => {
        if (url) imageMap[id] = url;
      });

      setImageUrls(imageMap);

      setPageInfo((prev) => ({
        ...prev,
        totalPage: result?.data?.meta?.total_page,
      }));
    } else {
      catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  useEffect(() => {
    getList();
  }, [pageInfo?.page, resetTrigger]);

  const fetchUploadedImage = async (fileName, originalFileName) => {
    try {
      const response = await getResizeFileDownload(fileName, originalFileName);
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const mimeType = response.headers["content-type"];
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error("이미지 불러오기 실패:", error);
      return null;
    }
  };

  const checkEach = (e) => {
    e.stopPropagation();
    let all = $(".request-check").length;
    let checked = $(".request-check:checked").length;
    let arr = [];
    if (all !== checked) $(".request-all-check").prop("checked", false);
    else $(".request-all-check").prop("checked", true);
    for (let i = 0; i < all; i++) {
      if (document.getElementsByClassName("request-check")[i].checked) {
        const val = document.getElementsByClassName("request-check")[i].value;
        arr.push(val);
        setIdArr(arr);
      }
    }
  };

  const deleteSelf = async () => {
    const data = idArr;
    const result = await requestMultiDelete(data);
    if (typeof result === "object") {
      commonModalSetting(setAlertBox, true, "alert", "삭제되었습니다.");
      $(".request-all-check").prop("checked", false);
      $(".request-check").prop("checked", false);
      changeState(setPageInfo, "page", 1);
      setIdArr([]);
      setAlert("completeDelete");

      getList();

      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "정상적으로 삭제되었습니다."
      );
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const checkAll = () => {
    let arr = [];
    if ($(".request-all-check").is(":checked")) {
      $(".request-check").prop("checked", true);
      const all = $(".request-check").length;
      for (let i = 0; i < all; i++) {
        const val = document.getElementsByClassName("request-check")[i].value;
        arr.push(val);
        setIdArr(arr);
      }
    } else {
      $(".request-check").prop("checked", false);
      setIdArr([]);
    }
  };

  const handleSearch = () => {
    setPageInfo((prev) => ({
      ...prev,
      page: 1,
    }));
    getList();
    updateQueryParams({ ...searchData, page: 1 })
  };

  const handleReset = () => {
    setSearchData({
      markName: "",
      templateName: "",
      applicantName: "",
      startDate: "",
      endDate: "",
      markType: "all",
    });
    setPageInfo((prev) => ({
      ...prev,
      page: 1,
    }));
    resetQueryParams();
    setResetTrigger((prev) => {
      const queryString = getQueryString();
      if (!queryString) return prev
      return !prev
    })
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 검토 요청 관리";
  }, []);

  return (
    <PageContainer 
      title="REQUEST-REVIEW" 
      icon={<FaClipboardList />}
    >
      {/* 검색 영역 */}
      <SearchCard>
        {/* 첫 번째 행 */}
        <div style={commonStyles.formRow}>
          <FormField label="출원상표명">
            <Input
              placeholder="출원 상표명을 입력해 주세요."
              value={searchData?.markName}
              onChange={(e) =>
                changeState(setSearchData, "markName", e.target.value)
              }
            />
          </FormField>
          <FormField label="템플릿명">
            <Input
              placeholder="템플릿명을 입력해 주세요."
              value={searchData?.templateName}
              onChange={(e) =>
                changeState(setSearchData, "templateName", e.target.value)
              }
            />
          </FormField>
        </div>

        {/* 두 번째 행 */}
        <div style={commonStyles.formRow}>
          <FormField label="출원인">
            <Input
              placeholder="출원인을 입력해 주세요."
              value={searchData?.applicantName}
              onChange={(e) =>
                changeState(setSearchData, "applicantName", e.target.value)
              }
            />
          </FormField>
          <DateRangeField
            label="신청일"
            startValue={searchData.startDate}
            endValue={searchData.endDate}
            onStartChange={(value) => changeState(setSearchData, "startDate", value)}
            onEndChange={(value) => changeState(setSearchData, "endDate", value)}
            maxDate={today}
          />
        </div>

        {/* 세 번째 행 */}
        <div style={{...commonStyles.formRow, marginBottom: 0}}>
          <FormField label="상표유형">
            <Select
              value={searchData.markType}
              onChange={(e) =>
                changeState(setSearchData, "markType", e.target.value)
              }
            >
              <option value="all">선택</option>
              <option value="text">문자상표</option>
              <option value="figure">도형상표</option>
              <option value="compound">복합상표</option>
            </Select>
          </FormField>
          <div style={{...commonStyles.formField, display: 'flex', alignItems: 'end'}}>
            <div style={{ display: 'flex', gap: designTokens.spacing.md }}>
              <Button variant="primary" icon={<FaSearch />} onClick={handleSearch}>
                검색
              </Button>
              <Button variant="secondary" icon={<FaUndo />} onClick={handleReset}>
                초기화
              </Button>
            </div>
          </div>
        </div>
      </SearchCard>

      {/* 액션 섹션 */}
      <div style={pageStyles.actionSection}>
        <Button
          variant="danger"
          icon={<FaTrash />}
          onClick={() => {
            if (!idArr.length)
              return commonModalSetting(
                setAlertBox,
                true,
                "alert",
                "삭제할 글을 선택해 주세요."
              );
            else {
              setAlert("deleteConfirm");
              commonModalSetting(
                setAlertBox,
                true,
                "confirm",
                "삭제하면 다시 복구할 수 없습니다.<br/>정말 삭제하시겠습니까?"
              );
            }
          }}
        >
          삭제하기
        </Button>
      </div>

      {/* 테이블 */}
      <TableContainer>
        <Table>
          <colgroup>
            <col width="5%" />
            <col width="8%" />
            <col width="15%" />
            <col width="15%" />
            <col width="12%" />
            <col width="12%" />
            <col width="15%" />
            <col width="10%" />
            <col width="13%" />
          </colgroup>
          <thead>
            <tr>
              <TableHeader>
                <div style={pageStyles.checkboxContainer}>
                  <input
                    type="checkbox"
                    className="request-all-check"
                    onChange={checkAll}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                </div>
              </TableHeader>
              <TableHeader>No.</TableHeader>
              <TableHeader>템플릿명</TableHeader>
              <TableHeader>출원상표명</TableHeader>
              <TableHeader>상표유형</TableHeader>
              <TableHeader>출원인</TableHeader>
              <TableHeader>사용자정보</TableHeader>
              <TableHeader>검토여부</TableHeader>
              <TableHeader>등록 일자</TableHeader>
            </tr>
          </thead>

          <tbody>
            {selfData?.length > 0 ? (
              selfData?.map(
                ({
                  id,
                  template_id,
                  template_name,
                  mark_name,
                  mark_type,
                  applicant_name,
                  member_info,
                  review_status,
                  created_at,
                }) => {
                  const checkDragText = (e) => {
                    const selection = window.getSelection();
                    if (selection.toString().length > 0) {
                      return;
                    }
                    navigate(`/review-self/${id}`);
                  };

                  const statusConfig = getReviewStatusConfig(review_status);

                  return (
                    <tr key={id}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div style={pageStyles.checkboxContainer}>
                          <input
                            type="checkbox"
                            className="request-check"
                            onChange={checkEach}
                            value={id}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        </div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={{ textAlign: 'center', fontWeight: '600' }}>{id}</div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={{ fontWeight: '500' }}>{template_name}</div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={pageStyles.markImageContainer}>
                          {imageUrls?.[id] && (
                            <img
                              src={imageUrls[id]}
                              alt="썸네일"
                              style={pageStyles.markImage}
                            />
                          )}
                          <span style={{ fontSize: '14px' }}>{mark_name}</span>
                        </div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <Badge variant={getMarkTypeBadgeVariant(mark_type)}>
                          {getMarkTypeText(mark_type)}
                        </Badge>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={{ fontWeight: '500' }}>
                          {applicant_name?.split(",")[0]}
                        </div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={pageStyles.userInfo}>
                          <div style={pageStyles.userName}>
                            {member_info?.user_name}
                          </div>
                          <div style={pageStyles.userId}>
                            ({member_info?.user_id})
                          </div>
                        </div>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <StatusIndicator status={statusConfig.variant}>
                          {statusConfig.icon}
                          {statusConfig.text}
                        </StatusIndicator>
                      </TableCell>

                      <TableCell onClick={checkDragText}>
                        <div style={{ fontSize: '13px' }}>
                          {created_at?.replace("T", " ")}
                        </div>
                      </TableCell>
                    </tr>
                  );
                }
              )
            ) : (
              <tr>
                <td colSpan="9" style={{
                  ...commonStyles.td, 
                  textAlign: 'center', 
                  padding: '48px', 
                  color: designTokens.colors.text.tertiary
                }}>
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 공통 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alertBox.mode === "confirm") deleteSelf();
            else if (alertBox === "completeDelete") getList();
            else if (alertBox === "logout") navigate("/");
            else return;
          }}
        />
      )}
    </PageContainer>
  );
};

export default ReviewPage;